import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../components-v2/Theme/theme';
import { ThinkingModalOverlayBackgroundImageUrl } from '../../../components/ai/AIContentGenerationWizard/models';
import {
	alternateTitleColor,
	background,
	brandPrimary,
	brandPrimaryHover,
	darkGrayFontColor,
	destructive,
	grayIconFill,
	header,
	inputBorderColor,
	mention,
	mentionDark,
	navigation,
	spanishBlue,
	titles,
} from '../../../styles/colors';
import { CssSelectors, CtaButtons, Layouts } from '../../../styles/styles';

const { spacing, typography } = THEME;
const detailsSidebarCardWidth = 350;

export const styleSheet = StyleSheet.create({
	thinking: {
		alignItems: 'center',
		boxSizing: 'border-box',
		display: 'flex',
		position: 'relative',
		...CssSelectors.allChildren({
			':not(:first-child)': {
				marginLeft: spacing.sm,
			},
			backgroundColor: '#C7D4DB',
			borderRadius: '50%',
			height: 12,
			width: 12,
		}),
	},
	pill: {
		alignItems: 'center',
		background: alternateTitleColor,
		borderRadius: 60,
		boxSizing: 'border-box',
		display: 'flex',
		height: 'auto',
		margin: spacing.none,
		padding: '14px 32px',
		position: 'relative',
		textAlign: 'center',
		fontSize: typography.fontSizes.xs,
		fontWeight: 'bold',
	},
	thinkingModalMessages: {
		...Layouts.verticalStack(15),
		alignItems: 'center',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
	},
	thinkingModal: {
		...CssSelectors.allChildren(
			{
				...CssSelectors.allChildren(
					{
						...CssSelectors.allChildren({
							':not(:first-child)': {
								marginTop: spacing.lg,
							},
						}),
						background: 'none',
						border: 'none',
						boxShadow: 'none',
						color: '#fff',
						marginTop: '12vh',
						padding: spacing.none,
						textAlign: 'center',
					},
					'.modal-overlay-content'
				),
				alignItems: 'flex-start',
				background: 'none',
				backgroundImage: `url(${ThinkingModalOverlayBackgroundImageUrl})`,
				backgroundSize: 'cover',
			},
			'.modal-overlay'
		),
	},
	aiAssistantWrap: {
		marginBottom: spacing.sm,
	},
	bodyEditor: {
		...CssSelectors.allDescendants(
			{
				overflow: 'inherit',
			},
			'.tox.tox-tinymce'
		),
		...CssSelectors.allDescendants(
			{
				overflow: 'inherit',
			},
			'.tox.tox-tinymce .tox-editor-container'
		),
		...CssSelectors.allDescendants(
			{
				background: '#fff',
				borderBottom: '1px solid #ccc',
				position: 'sticky',
				top: spacing.none,
				zIndex: 1,
			},
			'.tox.tox-tinymce .tox-editor-container .tox-editor-header'
		),
	},
	contentWrap: {
		padding: '20px 20px 68px 35px',
	},
	container: {
		border: `1px solid #CAE0EC`,
		flex: '1 0 0%',
		borderRadius: 3,
		marginTop: spacing.md,
		marginLeft: spacing.md,
		marginBottom: spacing.md,
		boxSizing: `border-box`,
		boxShadow: `0 0 12px 0 #E8F3F9`,
		overflow: `auto`,
	},
	subHeader: {
		borderBottom: `1px solid #CAE0EC`,
		borderRadius: 3,
		boxSizing: 'border-box',
		padding: '12px 20px 12px 35px',
	},
	subHeaderContent: {
		boxSizing: 'border-box',
		display: 'flex',
		alignItems: 'center',
		flex: 1,
		...CssSelectors.allChildren(
			{
				flex: 1,
				margin: spacing.none,
				color: brandPrimary,
			},
			'h1'
		),
	},
	contentBlock: {
		marginBottom: 68,
	},
	contentContainers: {
		color: '#a7abad',
		fontSize: typography.fontSizes.sm,
	},
	dropdownAnchorText: {
		maxWidth: 600,
	},
	dropdownMenu: {
		background: '#fff',
		border: '1px solid #E2E2E2',
		boxShadow: '0 0 6px 0 rgba(184,184,184,0.30)',
		marginTop: 21,
		maxHeight: 300,
		overflowY: 'auto',
		width: '100%',
	},
	dropdownInline: {
		borderColor: inputBorderColor,
		borderRadius: 3,
		borderStyle: 'solid',
		borderWidth: 1,
		boxSizing: 'border-box',
		maxWidth: 600,
		overflowY: 'visible',
		padding: 10,
		width: '100%',
	},
	dropdownItem: {
		':hover': {
			background: mention,
		},
		cursor: 'pointer',
		padding: 10,
		...CssSelectors.allChildren({
			color: navigation,
			fontSize: spacing.xs,
		}),
	},
	footer: {
		display: 'flex',
		margin: '20px 0 0',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		...CssSelectors.allChildren(
			{
				margin: '0 0 10px',
			},
			'button'
		),
	},
	reject: {
		color: destructive,
	},
	// Spacing for the approve action when it sits inside the compliance card footer, matching the gap the
	// email/social card uses between its send actions and approval buttons.
	complianceApproveFooter: {
		marginTop: 10,
	},
	sendFromDropdownContainer: {
		color: darkGrayFontColor,
		fontSize: typography.fontSizes.sm,
		marginBottom: spacing.none,
		marginTop: 10,
		width: '100%',
	},
	sendFromDropdown: {
		marginLeft: '0.5rem',
		maxWidth: 260,
		width: '80%',
	},
	addMediaContainer: {
		display: 'flex',
		justifyContent: 'space-between',
		marginTop: 14,
		maxWidth: 365,
	},
	mainContainer: {
		display: 'flex',
		width: '100%',
		height: '100%',
		gap: spacing.md,
	},
	menu: {
		display: 'block',
		...CssSelectors.allChildren(
			{
				display: 'inline-flex',
				marginRight: 10,
			},
			'li'
		),
		...CssSelectors.allChildren(
			{
				marginRight: spacing.none,
			},
			'li:nth-child(2)'
		),
	},
	sidebar: {
		width: 390,
		minWidth: 390,
		flexShrink: 0,
	},
	sidebarCenter: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},
	detailsSidebarCard: {
		marginRight: spacing.none,
		maxWidth: detailsSidebarCardWidth,
		width: '100%',
	},
	question: {
		color: titles,
		fontSize: typography.fontSizes.lg,
		marginTop: 42,
		marginBottom: 36,
		width: 236,
		textAlign: 'center',
	},
	formControl: {
		marginBottom: 18,
		...CssSelectors.allDescendants(
			{
				display: 'block',
				marginBottom: 12,
				fontSize: typography.fontSizes.xs,
				color: header,
				...CssSelectors.allChildren(
					{
						color: destructive,
					},
					'sup'
				),
			},
			'label'
		),
	},
	editor: {
		background,
		border: '1px solid #ccc',
		minHeight: 273,
		height: '33vh',
		margin: '0 0 12px 0',
		width: '100%',
		borderRadius: spacing.sm,
		...CssSelectors.allDescendants(
			{
				borderRadius: '8px 8px 0 0',
				borderBottom: `1px solid ${mentionDark}`,
			},
			'.tox-toolbar-overlord'
		),
	},
	iconButtons: {
		background,
		border: `1px solid ${mentionDark}`,
		color: titles,
		display: 'flex',
		fontSize: typography.fontSizes.md,
		marginBottom: spacing.xs,
		minWidth: 142,
		padding: spacing.sm,
	},
	freeImage: {
		paddingLeft: 6,
		paddingRight: 6,
	},
	sourceMention: {
		color: grayIconFill,
		fontSize: typography.fontSizes.xs,
		fontStyle: 'italic',
		minWidth: 142,
		textAlign: 'center',
	},
	ownImage: {
		color: spanishBlue,
		fontSize: typography.fontSizes.sm,
		marginBottom: 13,
		marginTop: 12,
	},
	mediaModalStyle: {
		width: 680,
	},
	subTitle: {
		color: brandPrimary,
		fontSize: typography.fontSizes.md,
		margin: '12px 0 0',
	},
	image: {
		width: '100%',
		height: '100%',
		objectFit: 'cover',
	},
	imageContainer: {
		display: 'flex',
		alignItems: 'flex-start',
		paddingTop: 22,
	},
	imageWrap: {
		width: 305,
		height: 203,
	},
	imageSection: {
		marginLeft: 20,
		...CssSelectors.allChildren(
			{
				fontSize: typography.fontSizes.sm,
				marginBottom: 12,
				color: titles,
			},
			'h3'
		),
		...CssSelectors.allChildren(
			{
				color: header,
			},
			'p'
		),
	},
	imageButton: {
		cursor: 'pointer',
		color: brandPrimary,
		textDecoration: 'underline',
		background: 'none',
		border: 'none',
		padding: spacing.none,
		fontWeight: 400,
		transitions: 'color 300ms ease-in-out',
		':hover': {
			color: brandPrimaryHover,
		},
	},
	loadingImages: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	aiKitCta: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
		marginBottom: spacing.md,
	},
	contentLabelRow: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: spacing.xs,
	},
	copyDropdownTrigger: {
		...CtaButtons.ctaButtonReverseSmall,
		gap: spacing.sm,
		height: 'auto',
	},
});
